import React, { useState } from 'react';
import {
  MoreVertical,
  Pencil,
  Trash2,
  Layers2,
  Lock,
  Sparkles,
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { FolderType } from '../../../types';
import FolderIcon from '../../library/folders/actions/FolderIcon';
import FolderAccessModal from '../../library/folders/actions/FolderAccessModal';
import FolderModal from '../../library/folders/FolderModal';
import useUserTags from '../../../hooks/useTags';

type FolderMenuProps = {
  folder: FolderType;
  onRename?: () => void;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const FolderMenu: React.FC<FolderMenuProps> = ({
  folder,
  onRename,
  triggerRef,
  open,
  onOpenChange,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [iconDialogOpen, setIconDialogOpen] = useState(false);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [askAIModalOpen, setAskAIModalOpen] = useState(false);
  const { getTag } = useUserTags();

  const folderIconsTag = getTag('folder_icons');
  const folderIcons = folderIconsTag ? JSON.parse(folderIconsTag) : {};
  const currentIcon = folderIcons[folder._id] || 'Folder';

  const handleRename = () => {
    // If onRename callback is provided, use inline rename
    if (onRename) {
      onRename();
    } else {
      // Fallback to modal if not in sidebar context
      dispatch(
        pushModal({
          type: 'rename-folder-modal' as any,
          files: [folder],
        } as any),
      );
    }
  };

  const handleDelete = () => {
    dispatch(
      pushModal({
        type: 'delete-folder-modal' as any,
        folderId: folder._id,
        files: [folder],
      } as any),
    );
  };

  const handleIconChange = () => {
    setIconDialogOpen(true);
  };

  const handleAccessModal = () => {
    setAccessModalOpen(true);
  };

  const handleAskAI = () => {
    setAskAIModalOpen(true);
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={handleAskAI}>
            <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.75} />
            {t('library.folder.actions.askAI', 'Ask AI')}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleRename}>
            <Pencil className="mr-2 h-4 w-4" strokeWidth={1.75} />
            {t('library.folder.actions.rename', 'Rename')}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleIconChange}>
            <Layers2 className="mr-2 h-4 w-4" strokeWidth={1.75} />
            {t('library.folder.actions.changeIcon', 'Change Icon')}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleAccessModal}>
            <Lock className="mr-2 h-4 w-4" strokeWidth={1.75} />
            {t('library.folder.actions.folderAccess', 'Folder Access')}
          </DropdownMenuItem>

          {/* Commented out - use team member sharing instead */}
          {/* <DropdownMenuItem onClick={handleShareFolder}>
            <Share2 className="mr-2 h-4 w-4" strokeWidth={1.75} />
            {t('library.folder.actions.share', 'Share Folder')}
          </DropdownMenuItem> */}

          <DropdownMenuItem
            onClick={handleDelete}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.75} />
            {t('library.folder.actions.delete', 'Delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {iconDialogOpen && (
        <FolderIcon
          open={iconDialogOpen}
          folderId={folder._id}
          currentIcon={currentIcon}
          onClose={() => setIconDialogOpen(false)}
        />
      )}

      {accessModalOpen && (
        <FolderAccessModal
          open={accessModalOpen}
          folder={folder}
          onClose={() => setAccessModalOpen(false)}
        />
      )}

      {askAIModalOpen && (
        <FolderModal
          open={askAIModalOpen}
          folder={folder}
          onClose={() => setAskAIModalOpen(false)}
        />
      )}
    </>
  );
};

export default FolderMenu;
